"use client"

import { useEffect, useState } from "react"
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import SaveIcon from "@mui/icons-material/Save"
import { ThemeProvider } from "@mui/material/styles"
import { extractErrorMessage, formatHora, rrhhTheme } from "./rrhhUi.js"

const buildEmptyTurno = () => ({
  turno: {
    codigo: "",
    descripcion: "",
    horaEntrada: "",
    horaSalida: "",
    minutosDescanso: "0",
    minutosToleranciaIngreso: "0",
    horasBaseDiarias: "",
    observaciones: "",
  },
})

const mapTurnoToForm = (turnoDetail) => ({
  turno: {
    codigo: turnoDetail?.codigo || "",
    descripcion: turnoDetail?.descripcion || "",
    horaEntrada: turnoDetail?.horaEntrada ? formatHora(turnoDetail.horaEntrada) : "",
    horaSalida: turnoDetail?.horaSalida ? formatHora(turnoDetail.horaSalida) : "",
    minutosDescanso: String(turnoDetail?.minutosDescanso ?? 0),
    minutosToleranciaIngreso: String(turnoDetail?.minutosToleranciaIngreso ?? 0),
    horasBaseDiarias: turnoDetail?.horasBaseDiarias ?? "",
    observaciones: turnoDetail?.observaciones || "",
  },
})

const ModalRrhhTurno = ({
  open,
  mode = "create",
  turnoDetail,
  loading = false,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState(buildEmptyTurno())
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (open) {
      setSaving(false)
      setError(null)
      setFormData(mode === "edit" ? mapTurnoToForm(turnoDetail) : buildEmptyTurno())
    }
  }, [open, mode, turnoDetail])

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({
      turno: {
        ...prev.turno,
        [field]: event.target.value,
      },
    }))
  }

  const handleSubmit = async () => {
    if (!formData.turno.descripcion.trim()) {
      setError("La descripción del turno es requerida.")
      return
    }

    if (!formData.turno.horaEntrada || !formData.turno.horaSalida) {
      setError("La hora de entrada y salida son requeridas.")
      return
    }

    try {
      setSaving(true)
      setError(null)
      await onSave(formData)
    } catch (submitError) {
      setError(extractErrorMessage(submitError, "No se pudo guardar el turno."))
    } finally {
      setSaving(false)
    }
  }

  return (
    <ThemeProvider theme={rrhhTheme}>
      <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {mode === "edit" ? "Editar turno" : "Nuevo turno"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Plantilla horaria reutilizable del módulo RRHH.
          </Typography>
        </DialogTitle>

        <DialogContent dividers sx={{ py: 3 }}>
          <Stack spacing={3}>
            {error ? <Alert severity="error">{error}</Alert> : null}

            {loading ? (
              <Typography variant="body2" color="text.secondary">
                Cargando datos del turno...
              </Typography>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Código"
                    value={formData.turno.codigo}
                    onChange={handleChange("codigo")}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <TextField
                    label="Descripción"
                    value={formData.turno.descripcion}
                    onChange={handleChange("descripcion")}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Hora entrada"
                    type="time"
                    value={formData.turno.horaEntrada}
                    onChange={handleChange("horaEntrada")}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 60 }}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Hora salida"
                    type="time"
                    value={formData.turno.horaSalida}
                    onChange={handleChange("horaSalida")}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 60 }}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    label="Descanso (min)"
                    value={formData.turno.minutosDescanso}
                    onChange={handleChange("minutosDescanso")}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    label="Tolerancia (min)"
                    value={formData.turno.minutosToleranciaIngreso}
                    onChange={handleChange("minutosToleranciaIngreso")}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    label="Horas base"
                    value={formData.turno.horasBaseDiarias}
                    onChange={handleChange("horasBaseDiarias")}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Observaciones"
                    value={formData.turno.observaciones}
                    onChange={handleChange("observaciones")}
                    fullWidth
                    multiline
                    minRows={2}
                  />
                </Grid>
              </Grid>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSubmit} disabled={saving || loading}>
            {saving ? "Guardando..." : mode === "edit" ? "Guardar cambios" : "Crear turno"}
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  )
}

export default ModalRrhhTurno
